import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@mlm/ui/components/toast';
import type { CoachingProfilePatch } from '@mlm/shared';
import {
  COACHING_FOCUS_KEYS,
  COACHING_FOCUS_LABELS,
  COACHING_STYLES,
  COACHING_STYLE_KEYS,
} from '@mlm/shared/coaching';
import { BirthDetailsFields } from '../components/birth-details';
import { birthDraftError, birthDraftFrom, birthPatch } from '../lib/birth';
import {
  CoachButton,
  ErrorNote,
  Eyebrow,
  Field,
  inputClass,
  Loading,
  Row,
  Screen,
} from '../components/primitives';
import { coaching, logout, type CoachingProfile } from '../lib/api';
import { browserTimeZone } from '../lib/format';
import { qk, useProfile } from '../lib/queries';

/** SC-41. Each section saves on its own. */
export function SettingsPage() {
  const profile = useProfile();
  if (profile.isPending) return <Loading />;
  if (profile.isError)
    return <ErrorNote error={profile.error} retry={() => void profile.refetch()} />;
  return <SettingsForm profile={profile.data.profile} />;
}

function SettingsForm({ profile }: { profile: CoachingProfile }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [birth, setBirth] = useState(() => birthDraftFrom(profile));
  const [birthError, setBirthError] = useState<string | null>(null);
  const [focus, setFocus] = useState(profile.focusCategory ?? '');
  const [style, setStyle] = useState(profile.coachingStyle ?? 'balanced');
  const save = useMutation({
    mutationFn: ({ patch }: { patch: CoachingProfilePatch; message: string }) =>
      coaching.updateProfile(patch),
    onSuccess: (_data, vars) => {
      toast.success(vars.message);
      void qc.invalidateQueries({ queryKey: qk.root });
    },
  });
  const deviceZone = browserTimeZone();

  return (
    <Screen back="/profile" eyebrow="Settings" title="Settings">
      <section className="space-y-4 border-b border-border px-5 py-6">
        <Eyebrow>Personal information</Eyebrow>
        <Field label="First name" htmlFor="settings-first-name">
          <input
            id="settings-first-name"
            className={inputClass}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={100}
          />
        </Field>
        <CoachButton
          disabled={!firstName.trim() || save.isPending}
          onClick={() =>
            save.mutate({ patch: { firstName: firstName.trim() }, message: 'Name saved.' })
          }
        >
          Save name
        </CoachButton>
      </section>

      <section className="space-y-4 border-b border-border px-5 py-6">
        <Eyebrow>Birth details</Eyebrow>
        <BirthDetailsFields value={birth} onChange={setBirth} />
        {birthError && (
          <p role="alert" className="text-sm text-destructive">
            {birthError}
          </p>
        )}
        <CoachButton
          disabled={save.isPending}
          onClick={() => {
            const problem = birthDraftError(birth);
            setBirthError(problem);
            if (!problem)
              save.mutate({
                patch: birthPatch(birth),
                message: 'Birth details saved. Your chart will be recalculated.',
              });
          }}
        >
          Save birth details
        </CoachButton>
      </section>

      <section className="space-y-4 border-b border-border px-5 py-6">
        <Eyebrow>Coaching</Eyebrow>
        <Field label="Primary focus" htmlFor="settings-focus">
          <select
            id="settings-focus"
            className={inputClass}
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
          >
            <option value="">Not set</option>
            {COACHING_FOCUS_KEYS.map((key) => (
              <option key={key} value={key}>
                {COACHING_FOCUS_LABELS[key]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Coaching style"
          htmlFor="settings-style"
          hint={COACHING_STYLES[style as keyof typeof COACHING_STYLES]?.description}
        >
          <select
            id="settings-style"
            className={inputClass}
            value={style}
            onChange={(e) => setStyle(e.target.value)}
          >
            {COACHING_STYLE_KEYS.map((key) => (
              <option key={key} value={key}>
                {COACHING_STYLES[key].label}
              </option>
            ))}
          </select>
        </Field>
        <CoachButton
          disabled={save.isPending}
          onClick={() =>
            save.mutate({
              patch: {
                focusCategory: (focus || null) as CoachingProfilePatch['focusCategory'],
                coachingStyle: style as CoachingProfilePatch['coachingStyle'],
              },
              message: 'Coaching preferences saved.',
            })
          }
        >
          Save coaching preferences
        </CoachButton>
      </section>

      <section className="space-y-3 border-b border-border px-5 py-6">
        <Eyebrow>Timezone</Eyebrow>
        <p className="text-[0.95rem] text-ink">{profile.timezone}</p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Your day, score and streak follow this timezone.
        </p>
        {deviceZone !== profile.timezone && (
          <CoachButton
            disabled={save.isPending}
            onClick={() =>
              save.mutate({ patch: { timezone: deviceZone }, message: 'Timezone updated.' })
            }
          >
            Use this device's timezone ({deviceZone})
          </CoachButton>
        )}
      </section>

      <Row
        title="Notifications"
        meta="Morning and evening reminders"
        to="/settings/notifications"
      />

      <section className="flex flex-wrap items-center gap-5 px-5 py-6">
        <Link to="/change-password" className="eyebrow underline underline-offset-4 hover:text-ink">
          Change password
        </Link>
        <CoachButton
          kind="quiet"
          onClick={() => void logout().then(() => navigate('/welcome', { replace: true }))}
        >
          Log out
        </CoachButton>
      </section>
    </Screen>
  );
}
